/** Catálogo SA4 del editor de planes.
 * Espejo de backend/core/plan_access.py (MENU_MODULES, MODULE_INHERIT_FROM,
 * PLAN_CAPABILITIES). El backend es la fuente de verdad al guardar; esto
 * solo pinta el árbol y hereda llaves nuevas al abrir un plan viejo.
 */
import { APP_NAME } from '@/lib/brand';

export const MODULE_INHERIT_FROM = {
  agenda: 'leads',
  file_library: 'ai_agent',
  waba: 'settings',
  api_hub: 'settings',
  landing: 'settings',
  cem_ratings: 'settings',
};

export const SUPERADMIN_ALWAYS_MODULES = ['settings', 'waba', 'api_hub', 'landing', 'cem_ratings', 'capi'];

export const PLAN_MODULE_TREE = [
  {
    group: 'CRM',
    tip: 'Lo que ve el vendedor día a día.',
    items: [
      { key: 'dashboard', label: 'Dashboards', tip: 'Tablero de ventas, IA y CallForce. Super Admin siempre entra a configurar.' },
      { key: 'leads', label: 'Leads', tip: 'Lista, ficha 360 y duplicidad. El tope de leads/mes vive en Caps.', cap: 'contacts_monthly' },
      { key: 'agenda', label: 'Agenda', tip: 'Citas del vendedor. Antes colgaba de Leads: si el plan viejo no la declara, copia a Leads.' },
      { key: 'opportunities', label: 'Oportunidades', tip: 'Kanban y etapas. Las reglas del pipeline son solo admin.' },
      { key: 'properties', label: 'Propiedades', tip: 'Inventario inmobiliario. El plan lo controla. Excepción: en catálogo (educación/general/retail) se apaga por industria.' },
      { key: 'developments', label: 'Desarrollos', tip: 'Torres y cerebro por desarrollo. Mutuamente excluyente con Productos. El plan lo controla.' },
      { key: 'brands', label: 'Marcas', tip: 'Vertical productos / colegios / negocio general.' },
      { key: 'products', label: 'Productos y Servicios', tip: 'Catálogo de General y Educación (fotos, PDF, video, YouTube). Mutuamente excluyente con Desarrollos. En inmobiliaria vive en Team Config.' },
      { key: 'car_dealer', label: 'Car-Dealer', tip: 'Inventario de agencia de autos.' },
      { key: 'car_builder', label: 'Car Builder', tip: `Landing de autos de ${APP_NAME}. Chevrolet /l/ no se toca.` },
    ],
  },
  {
    group: 'Comunicación',
    tip: 'Canales y automatización. Un plan sin voz no vende voz en la landing.',
    items: [
      { key: 'mass_campaigns', label: 'WhatsApp / Voice Blaster', tip: 'Envíos masivos WA y llamadas IA. Caps: envíos/mes y minutos IA.' },
      { key: 'marketing', label: 'Correo', tip: 'Campañas de correo y paso email de recetas. Cap: correos/mes.' },
      { key: 'comm_logics', label: 'Reglas de Comunicación', tip: 'Seguimiento automático (formulario vs WhatsApp). El mismo nombre que ve el tenant en su menú.' },
      { key: 'ai_agent', label: 'Conversaciones IA', tip: 'Bandeja del asistente. Cap interno: tokens OpenRouter.' },
      { key: 'file_library', label: 'Biblioteca', tip: 'Folletos y videos para la IA. Cap: GB. Antes colgaba del Agente IA.' },
      { key: 'ai_independent_campaigns', label: 'Campañas IA (Config → Asistente IA)', tip: 'Vive en Configuración → Asistente IA (subtab), no es menú de primer nivel. Se muestra en el preview cuando está activo.' },
      { key: 'waba', label: 'WhatsApp WABA', tip: 'Cuentas y plantillas. Antes colgaba de Configuración.' },
    ],
  },
  {
    group: 'Equipo y config',
    tip: 'El admin del tenant configura; Super Admin siempre puede entrar.',
    items: [
      { key: 'team', label: 'Team Config', tip: 'Usuarios, guardias y grupos. Cap: usuarios. El plan lo controla.' },
      { key: 'commissions', label: 'Comisiones', tip: 'Solo inmobiliaria / franquicia / independiente.' },
      { key: 'settings', label: 'Configuración', tip: 'Pantalla del tenant. Super Admin impersonando SIEMPRE entra, aunque el plan la tenga off.', saAlways: true },
      { key: 'cem_ratings', label: 'Calificaciones Forecast', tip: 'Catálogo CEM. Antes colgaba de Configuración.' },
      { key: 'landing', label: 'White Label', tip: 'White label de agencia/desarrolladora. Autos usan Car Builder.' },
      { key: 'api_hub', label: 'API Hub', tip: 'Keys, leads-correo y conectores.' },
      { key: 'capi', label: 'CAPI Meta', tip: 'Token + mapeo etapa → evento. Nace OFF. Super Admin impersonando siempre entra. No es menú de primer nivel (vive en Configuración).', defaultOff: true, saAlways: true },
      { key: 'academy', label: `${APP_NAME} Academy`, tip: 'Cursos internos.' },
      { key: 'vcard', label: 'Mi tarjeta virtual', tip: 'Siempre estuvo visible. Default ON. El menú no la gatea (candado Alexis).' },
    ],
  },
];

export const PLAN_MODULE_OPTIONS = PLAN_MODULE_TREE.flatMap((g) => g.items.map(({ key, label }) => ({ key, label })));

export const DEFAULT_PLAN_MODULES = (() => {
  const base = Object.fromEntries(PLAN_MODULE_OPTIONS.map((item) => [item.key, true]));
  base.car_dealer = false;
  base.car_builder = false;
  base.brands = false;
  base.products = false;
  base.capi = false;
  return base;
})();

export function normalizePlanModules(stored) {
  const src = stored && typeof stored === 'object' ? stored : {};
  const normalized = { ...DEFAULT_PLAN_MODULES };
  Object.entries(src).forEach(([key, value]) => {
    if (key in normalized) normalized[key] = Boolean(value);
  });
  Object.entries(MODULE_INHERIT_FROM).forEach(([nuevo, padre]) => {
    if (nuevo in src) return;
    if (padre && padre in normalized) normalized[nuevo] = Boolean(normalized[padre]);
  });
  if (normalized.products && normalized.developments) normalized.developments = false;
  return normalized;
}

export const CAPABILITY_OPTIONS = [
  { key: 'voice', group: 'Voz y llamadas', label: 'Voz / IVR', on: true, tip: 'Llamadas del sistema (IVR y puente lead↔asesor). YA está en producción: déjalo encendido salvo que quieras venderlo aparte. Un plan sin esto no vende voz en la landing.' },
  { key: 'voice_ai_outbound', group: 'Voz y llamadas', label: 'Llamadas IA salientes', on: false, tip: 'La IA marca a los leads. NUEVO, apagado por defecto.' },
  { key: 'voice_ai_inbound', group: 'Voz y llamadas', label: 'Llamadas IA entrantes', on: false, tip: 'La IA ATIENDE llamadas. NUEVO, apagado por defecto.' },
  { key: 'voice_realtime', group: 'Voz y llamadas', label: 'Voz IA en vivo (conversación natural)', on: false, tip: 'La IA conversa en tiempo real (no por turnos). Requiere pegar la llave del proveedor en Integraciones y elegir este motor en Usabilidad.' },
  { key: 'recepcion_ia', group: 'Voz y llamadas', label: 'Recepción IA', on: false, tip: 'El cliente marca el número del anuncio. No enciende sola: falta número + interruptor por empresa.' },
  { key: 'callforce', group: 'Dirección / Ejecutivo', label: 'CallForce (análisis de llamadas)', on: true, tip: 'Pestaña del Dashboard que transcribe y califica cada llamada. YA activo en producción. Es una vista de dirección, no automatización de voz.' },
  { key: 'direccion', group: 'Dirección / Ejecutivo', label: 'Panel Ejecutivo / Dirección', on: true, tip: 'La pestaña Dirección del Dashboard: Asistente Ejecutivo IA (C-Suite) para directores. Default ON (nadie lo pierde); apágalo en los planes donde no lo quieras vender.' },
];

export const DEFAULT_CAPABILITIES = Object.fromEntries(CAPABILITY_OPTIONS.map((c) => [c.key, c.on]));

export const DEFAULT_VOICE_POLICY = {
  max_ai_attempts_per_lead_24h: 5,
  attempt_window_hours: 24,
  defer_assignment_until_reply: true,
  release_to_roulette_after_max_attempts: true,
  outbound_cooldown_hours: 4,
  allowed_call_engines: ['bridge', 'realtime'],
};

export const PLAN_CAP_FIELDS = [
  { key: 'contacts_monthly', bag: 'monthly_caps', label: 'Leads / mes', tip: 'Tope de leads captados este mes. -1 = ilimitado. El backend ya corta al llegar (popup de upgrade).' },
  { key: 'users', bag: 'limits', label: 'Usuarios', tip: 'Cupo del equipo. Al llenarse, no se dan de alta más.' },
  { key: 'mass_campaign_sends', bag: 'monthly_caps', label: 'Envíos WA / mes', tip: 'Tope de plantillas/envíos de campañas masivas. -1 = ilimitado.' },
  { key: 'mass_campaign_leads', bag: 'monthly_caps', label: 'Leads de campañas / mes', tip: 'Cuántos contactos puede cargar una masiva este mes.' },
  { key: 'ai_outbound_calls', bag: 'monthly_caps', label: 'Llamadas IA / mes', tip: 'Tope de llamadas IA salientes. -1 = ilimitado. Inerte si el plan no tiene voz.' },
  { key: 'ai_call_minutes', bag: 'monthly_caps', label: 'Minutos IA / mes', tip: 'Tope de minutos Twilio+IA. -1 = ilimitado.' },
  { key: 'mass_campaign_ai_calls', bag: 'monthly_caps', label: 'Llamadas IA de masiva / mes', tip: 'Tope de Voice Blaster. -1 = ilimitado.' },
  { key: 'emails_monthly', bag: 'monthly_caps', label: 'Correos / mes', tip: 'Tope de correos de campaña. -1 = ilimitado. El motor ya existe; el corte en envío se engancha después.' },
  { key: 'storage_gb', bag: 'limits', label: 'Biblioteca (GB)', tip: 'Tope de espacio. -1 = ilimitado. No pisa el límite por-tenant que ya existe.' },
  { key: 'comm_recipe_call_steps', bag: 'limits', label: 'Pasos de llamada / receta', tip: 'Máximo de pasos tipo llamada en una receta. -1 = sin tope.' },
  { key: 'developments', bag: 'limits', label: 'Desarrollos', tip: 'Cuántos desarrollos puede crear (inmobiliaria). -1 = ilimitado. Ya se aplica en el backend.' },
  { key: 'brands', bag: 'limits', label: 'Marcas', tip: 'Cuántas marcas puede crear (comercial/catálogo). -1 = ilimitado.' },
  { key: 'products', bag: 'limits', label: 'Productos / Modelos', tip: 'Cuántos productos o modelos de auto puede crear. -1 = ilimitado.' },
];

const VENDOR_SKIP = new Set([
  'mass_campaigns', 'marketing', 'ai_independent_campaigns', 'waba', 'api_hub',
  'comm_logics', 'landing', 'team', 'settings', 'cem_ratings', 'capi',
]);

function canonBusinessType(raw) {
  if (raw === 'car-dealer') return 'car_dealer';
  if (raw === 'education' || raw === 'generic' || raw === 'retail') return 'agency';
  return raw || 'agency';
}

export function previewMenu(modules, { role = 'vendor', businessType = 'agency' } = {}) {
  const mods = normalizePlanModules(modules);
  const isVendor = role === 'vendor';
  const bt = canonBusinessType(businessType);
  const labels = [];
  PLAN_MODULE_OPTIONS.forEach(({ key, label }) => {
    if (isVendor && VENDOR_SKIP.has(key)) return;
    if (key === 'properties' && (bt === 'developer' || bt === 'car_dealer')) return;
    if (key === 'products' && (bt === 'developer' || bt === 'car_dealer')) return;
    if (key === 'developments' && !['developer', 'franchise'].includes(bt)) return;
    if (key === 'car_dealer' && bt !== 'car_dealer') return;
    if (key === 'car_builder' && bt !== 'car_dealer') return;
    if (key === 'commissions' && !['agency', 'franchise', 'independent'].includes(bt)) return;
    if (key === 'landing' && ['independent', 'car_dealer'].includes(bt)) return;
    if (!mods[key]) return;
    labels.push(label);
  });
  return labels;
}

export function capLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 'Ilimitado';
  if (n === 0) return '0 (bloqueado)';
  return String(n);
}

export function blankPlanForm(sortOrder = 1) {
  return {
    name: '', description: '', price: 0, original_price: 0, currency: 'MXN', billing_period: 'month',
    discount_pct: 0, discount_note: '', trial_days: 0, is_active: true, is_visible: true, is_popular: false,
    sort_order: sortOrder, target_audience: 'agency', license_type: 'agency',
    industry: '', industry_subtype: '', is_sas_only: false,
    period_discounts: { quarterly_pct: 5, semiannual_pct: 10, annual_pct: 15 },
    price_matrix: { monthly: 0, quarterly: 0, semiannual: 0, annual: 0 },
    superadmin_volume_pricing: { enabled: false, min_licenses: 5, discount_pct: 0 },
    visible_for_types: ['agency', 'independent', 'developer', 'franchise', 'owner', 'car_dealer'],
    included_integrations: {},
    included_modules: { ...DEFAULT_PLAN_MODULES },
    limits: { users: 1, contacts: 100, flows: 1, campaigns: 1, developments: -1, comm_recipe_call_steps: -1, storage_gb: -1 },
    monthly_caps: {
      mass_campaign_leads: -1, mass_campaign_sends: -1, contacts_monthly: -1,
      ai_outbound_calls: -1, ai_call_minutes: -1, mass_campaign_ai_calls: -1, emails_monthly: -1,
    },
    comm_logics_policy: { max_links: -1, lock_modification: false },
    locked_comm_logic_id: '',
    ai_tokens: { included: 0, monthly_limit: 0, block_on_exceed: true },
    capabilities: { ...DEFAULT_CAPABILITIES },
    voice_policy: { ...DEFAULT_VOICE_POLICY },
    features: [], campaign_features: [],
  };
}
